/**
 * What the MCP connector exposes, as the docs page states it publicly.
 *
 * <h3>Why this is a committed constant and not a fetch</h3>
 * The docs URL is part of a connector directory submission, and an empty or
 * failed-to-load docs page is a rejection. A page that fetches its own content
 * from the MCP server is one CORS header or one cold start away from rendering
 * nothing, so the catalogue ships with the bundle and always renders.
 *
 * <h3>Which means it can rot, so say the quiet part</h3>
 * It already did once: the tracker claimed ten tools, then fifteen, while
 * `src/tools.ts` had twenty-seven. The count below was read out of that file on
 * 6 October 2026, not recalled. `connectorCatalogue.test.ts` pins the totals so a
 * half-finished edit here fails, and `tools.annotations.test.ts` in the MCP repo
 * fails the moment a tool is added there — that is the pairing that keeps these
 * honest: the MCP test tells you a tool changed, this one tells you the docs
 * disagree with themselves.
 *
 * <p>Descriptions are deliberately written for the person granting consent, not
 * copied from the tool's own description, which is written for a model.
 */

export interface CatalogueTool {
  name: string;
  title: string;
  scope: ScopeName;
  /** True when the tool changes something. 10 of 41 do. */
  writes: boolean;
  /** True only for a tool that ends or removes something. Exactly one does. */
  destructive: boolean;
}

export type ScopeName =
  | 'whatsapp:send'
  | 'messages:read'
  | 'calls:originate'
  | 'calls:manage'
  | 'calls:read'
  | 'campaigns:read'
  | 'leads:read'
  | 'contacts:read'
  | 'metrics:read'
  | 'agents:read'
  | 'dnd:manage'
  | 'contacts:write'
  | 'campaigns:send'
  | 'billing:read'
  | 'leads:write'
  | 'agents:write'
  | 'billing:pay';

/**
 * The scopes, in the order the consent screen groups them.
 *
 * <p>`grants` is phrased as a capability the reader is handing over, because that
 * is the decision in front of them. Read and write are split per area on purpose:
 * somebody who wants an assistant to answer "how did the Diwali send do" should be
 * able to grant exactly that, and a consent screen where looking and sending share
 * one checkbox is one nobody reads twice.
 */
export const CONNECTOR_SCOPES: Array<{
  scope: ScopeName;
  area: string;
  grants: string;
  writes: boolean;
}> = [
  { scope: 'whatsapp:send', area: 'WhatsApp', grants: 'Send WhatsApp messages from your number', writes: true },
  { scope: 'messages:read', area: 'WhatsApp', grants: 'See whether a message was delivered or read', writes: false },
  { scope: 'calls:originate', area: 'Calls', grants: 'Place outbound calls from your caller IDs', writes: true },
  { scope: 'calls:manage', area: 'Calls', grants: 'End a call that is in progress', writes: true },
  { scope: 'calls:read', area: 'Calls', grants: 'Read your call logs, live calls and extension directory', writes: false },
  { scope: 'campaigns:read', area: 'Campaigns', grants: 'Read campaigns, how they landed, and your WhatsApp templates', writes: false },
  { scope: 'campaigns:send', area: 'Campaigns', grants: 'Create a WhatsApp campaign as a draft, then send or pause it once you confirm', writes: true },
  { scope: 'leads:read', area: 'Leads', grants: 'Read leads captured from calls, and the figures over them', writes: false },
  { scope: 'leads:write', area: 'Leads', grants: 'Correct a lead or add one by hand (never delete; your CRM is not updated)', writes: true },
  { scope: 'contacts:read', area: 'Contacts', grants: 'Read your contacts and their message history', writes: false },
  { scope: 'contacts:write', area: 'Contacts', grants: 'Add a contact and edit one (never delete)', writes: true },
  { scope: 'metrics:read', area: 'Reports', grants: 'Read the dashboard and report figures', writes: false },
  { scope: 'agents:read', area: 'AI agents', grants: 'Read your AI agents and how they are configured', writes: false },
  { scope: 'agents:write', area: 'AI agents', grants: "Change an agent's greeting, instructions or opening line", writes: true },
  { scope: 'dnd:manage', area: 'Do not contact', grants: 'Add somebody to your do-not-contact list, and check it', writes: true },
  { scope: 'billing:read', area: 'Billing', grants: 'Read your wallet balance, usage charges and invoices', writes: false },
  { scope: 'billing:pay', area: 'Billing', grants: 'Create a payment link to top up your wallet (owners only; credited when paid)', writes: true },
];

/** Read out of the MCP server's `src/tools.ts`; agents added 8 October 2026, 13 more 26 October 2026, lead/agent writes and wallet top-up after. */
export const CONNECTOR_TOOLS: CatalogueTool[] = [
  { name: 'whatsapp_send_text', title: 'Send WhatsApp text', scope: 'whatsapp:send', writes: true, destructive: false },
  { name: 'whatsapp_send_template', title: 'Send WhatsApp template', scope: 'whatsapp:send', writes: true, destructive: false },
  { name: 'whatsapp_message_status', title: 'WhatsApp message status', scope: 'messages:read', writes: false, destructive: false },
  { name: 'outbound_call', title: 'Place an outbound call', scope: 'calls:originate', writes: true, destructive: false },
  { name: 'hangup_call', title: 'Hang up a call', scope: 'calls:manage', writes: true, destructive: true },
  { name: 'call_status', title: 'Call status / log', scope: 'calls:read', writes: false, destructive: false },
  { name: 'call_list', title: 'List calls', scope: 'calls:read', writes: false, destructive: false },
  { name: 'call_summary', title: 'Call volume and duration totals', scope: 'calls:read', writes: false, destructive: false },
  { name: 'calls_live', title: 'Calls in progress right now', scope: 'calls:read', writes: false, destructive: false },
  { name: 'caller_ids', title: 'Numbers you can call from', scope: 'calls:read', writes: false, destructive: false },
  { name: 'directory', title: 'The internal extension directory', scope: 'calls:read', writes: false, destructive: false },
  { name: 'campaign_list', title: 'List WhatsApp campaigns', scope: 'campaigns:read', writes: false, destructive: false },
  { name: 'campaign_detail', title: 'Get one WhatsApp campaign', scope: 'campaigns:read', writes: false, destructive: false },
  { name: 'campaign_insights', title: 'How a campaign landed', scope: 'campaigns:read', writes: false, destructive: false },
  { name: 'lead_list', title: 'List captured leads', scope: 'leads:read', writes: false, destructive: false },
  { name: 'lead_detail', title: 'Get one captured lead', scope: 'leads:read', writes: false, destructive: false },
  { name: 'lead_insights', title: 'Lead capture figures', scope: 'leads:read', writes: false, destructive: false },
  { name: 'call_outcome', title: 'What a call achieved', scope: 'leads:read', writes: false, destructive: false },
  { name: 'contact_list', title: 'List contacts', scope: 'contacts:read', writes: false, destructive: false },
  { name: 'contact_search', title: 'Search contacts', scope: 'contacts:read', writes: false, destructive: false },
  { name: 'contact_detail', title: 'Get one contact', scope: 'contacts:read', writes: false, destructive: false },
  { name: 'contact_by_phone', title: 'Look up a contact by phone number', scope: 'contacts:read', writes: false, destructive: false },
  { name: 'contact_timeline', title: "A contact's message history", scope: 'contacts:read', writes: false, destructive: false },
  { name: 'metrics_summary', title: 'Dashboard and report metrics', scope: 'metrics:read', writes: false, destructive: false },
  { name: 'agent_list', title: 'List AI agents', scope: 'agents:read', writes: false, destructive: false },
  { name: 'agent_detail', title: 'Get one AI agent', scope: 'agents:read', writes: false, destructive: false },
  { name: 'dnd_add', title: 'Record that somebody must not be contacted', scope: 'dnd:manage', writes: true, destructive: false },
  { name: 'dnd_check', title: 'Whether somebody is already on the do-not-contact list', scope: 'dnd:manage', writes: false, destructive: false },
  { name: 'call_transcript', title: "A call's transcript", scope: 'calls:read', writes: false, destructive: false },
  { name: 'template_list', title: 'List WhatsApp templates', scope: 'campaigns:read', writes: false, destructive: false },
  { name: 'template_detail', title: 'Get one WhatsApp template', scope: 'campaigns:read', writes: false, destructive: false },
  { name: 'lead_export', title: 'Export leads as CSV', scope: 'leads:read', writes: false, destructive: false },
  { name: 'contact_create', title: 'Add a contact', scope: 'contacts:write', writes: true, destructive: false },
  { name: 'contact_update', title: 'Change a contact', scope: 'contacts:write', writes: true, destructive: false },
  { name: 'campaign_create', title: 'Create a WhatsApp campaign (draft)', scope: 'campaigns:send', writes: true, destructive: false },
  { name: 'campaign_launch', title: 'Launch a WhatsApp campaign', scope: 'campaigns:send', writes: true, destructive: false },
  { name: 'campaign_pause', title: 'Pause a WhatsApp campaign', scope: 'campaigns:send', writes: true, destructive: false },
  { name: 'wallet_balance', title: 'Prepaid wallet balance', scope: 'billing:read', writes: false, destructive: false },
  { name: 'billing_summary', title: "This month's usage and charges", scope: 'billing:read', writes: false, destructive: false },
  { name: 'invoice_list', title: 'List invoices', scope: 'billing:read', writes: false, destructive: false },
  { name: 'invoice_detail', title: 'Get one invoice', scope: 'billing:read', writes: false, destructive: false },
  { name: 'lead_update', title: 'Correct a lead', scope: 'leads:write', writes: true, destructive: false },
  { name: 'lead_create', title: 'Add a lead by hand', scope: 'leads:write', writes: true, destructive: false },
  { name: 'agent_update', title: "Change an AI agent's greeting or instructions", scope: 'agents:write', writes: true, destructive: true },
  { name: 'wallet_recharge_link', title: 'Create a wallet top-up payment link', scope: 'billing:pay', writes: true, destructive: false },
];

/** Totals the docs page prints, derived rather than typed into the prose. */
export const CATALOGUE_TOTALS = {
  tools: CONNECTOR_TOOLS.length,
  writes: CONNECTOR_TOOLS.filter((t) => t.writes).length,
  reads: CONNECTOR_TOOLS.filter((t) => !t.writes).length,
  scopes: CONNECTOR_SCOPES.length,
};
